import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { getApiError } from '../../lib/api';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { LogoMark, Wordmark } from '../../components/Logo';

type Step = 'credentials' | 'verify' | 'setup';

/** Group a base32 secret into 4-char blocks for easier manual entry. */
function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

export function LoginPage(): JSX.Element {
  const { user, login, verifyTotp, setupTotp, enableTotp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [setupInfo, setSetupInfo] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  const goHome = (): void => {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
    navigate(from, { replace: true });
  };

  const onSubmitCredentials = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await login(email, password);
      if (outcome.status === 'ok') {
        goHome();
        return;
      }
      setChallengeToken(outcome.challengeToken);
      if (outcome.mode === 'setup') {
        setSetupInfo(await setupTotp(outcome.challengeToken));
        setStep('setup');
      } else {
        setStep('verify');
      }
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitCode = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (step === 'setup') {
        await enableTotp(challengeToken, code);
      } else {
        await verifyTotp(challengeToken, code);
      }
      goHome();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-paper">
      <div className="flex-1 flex items-center justify-center px-6">
        {step === 'credentials' ? (
          <form onSubmit={onSubmitCredentials} className="w-full max-w-sm">
            <div className="mb-10">
              <Wordmark size="lg" />
              <div className="text-xs uppercase tracking-[0.22em] text-ink-3 mt-6">Sign in</div>
            </div>
            <div className="space-y-4">
              <Input
                id="email"
                label="Email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
              <Input
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              {error && (
                <div className="text-sm bg-negative-soft text-negative px-3 py-2 rounded border border-negative/20">
                  {error}
                </div>
              )}
              <Button type="submit" loading={submitting} className="w-full" size="lg">
                Sign in
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={onSubmitCode} className="w-full max-w-sm">
            <div className="mb-8">
              <Wordmark size="lg" />
              <div className="text-xs uppercase tracking-[0.22em] text-ink-3 mt-6">
                {step === 'setup' ? 'Set up two-factor' : 'Two-factor authentication'}
              </div>
            </div>

            {step === 'setup' && setupInfo && (
              <div className="mb-6 space-y-3">
                <p className="text-sm text-ink-3 leading-relaxed">
                  This account has remote access, so it needs an authenticator app. This is a
                  one-time setup:
                </p>
                <ol className="text-sm text-ink-2 space-y-1.5 list-decimal pl-5 marker:text-ink-3">
                  <li>
                    Install an authenticator app (Google Authenticator, Microsoft Authenticator,
                    Authy, or 1Password).
                  </li>
                  <li>Add an account and enter the setup key below, or use the link beneath it.</li>
                  <li>Enter the 6-digit code it generates to finish.</li>
                </ol>
                <div className="bg-paper-2 border border-rule rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wide text-ink-3 mb-1">
                    Setup key
                  </div>
                  <div className="kc-mono text-sm text-ink break-all select-all">
                    {formatSecret(setupInfo.secret)}
                  </div>
                </div>
                <a
                  href={setupInfo.otpauthUri}
                  className="text-xs text-accent hover:underline break-all"
                >
                  Open in authenticator app →
                </a>
              </div>
            )}

            {step === 'verify' && (
              <p className="text-sm text-ink-3 leading-relaxed mb-5">
                Enter the 6-digit code from your authenticator app to continue.
              </p>
            )}

            <div className="space-y-4">
              <Input
                id="totp"
                label="6-digit code"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                autoComplete="one-time-code"
              />
              {error && (
                <div className="text-sm bg-negative-soft text-negative px-3 py-2 rounded border border-negative/20">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                loading={submitting}
                disabled={code.length !== 6}
                className="w-full"
                size="lg"
              >
                {step === 'setup' ? 'Verify & enable' : 'Verify'}
              </Button>
              <button
                type="button"
                className="text-xs text-ink-3 hover:text-ink w-full text-center"
                onClick={() => {
                  setStep('credentials');
                  setCode('');
                  setError(null);
                  setSetupInfo(null);
                }}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Right column — quiet brand panel */}
      <div className="hidden lg:flex flex-1 bg-ink items-center justify-center relative overflow-hidden">
        <div className="absolute -right-20 -top-32 opacity-[0.08]">
          <LogoMark size={620} variant="dark" />
        </div>
        <div className="relative max-w-md px-12 text-paper-2">
          <LogoMark size={56} variant="dark" />
          <h2 className="kc-serif text-5xl leading-tight mt-8">
            Welcome <em className="text-accent-soft">back.</em>
          </h2>
          <p className="mt-6 text-base text-paper-2/70 leading-relaxed">
            Sign in to pick up where you left off, your everyday workspace for{' '}
            <span className="text-paper-2">Kalachand Mattress</span>.
          </p>
          <div className="mt-10 pt-6 border-t border-paper-2/15 text-[10px] uppercase tracking-[0.24em] text-paper-2/50">
            v 1.0 · Mauritius
          </div>
        </div>
      </div>
    </div>
  );
}
